import { Controller } from "@hotwired/stimulus"

// Handles agent test run submission via XHR (avoids nested form issue).
// Posts to the test_run endpoint as JSON, then navigates to the run show page via Turbo.
// Persists messages in URL params (for browser back) and localStorage (for history dropdown).
export default class extends Controller {
  static targets = ["message", "userId", "metaConnectId", "submit", "error", "history", "historyList"]
  static values = { url: String, agentId: String }

  connect() {
    // Restore message from URL params (browser back support)
    const params = new URLSearchParams(window.location.search)
    const savedMessage = params.get("test_message")
    if (savedMessage && this.hasMessageTarget) {
      this.messageTarget.value = savedMessage
    }

    // Populate message history
    this.renderHistory()
  }

  async run(event) {
    event.preventDefault()

    const btn = this.submitTarget
    const originalHTML = btn.innerHTML

    // Clear any previous error
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")

    // Loading state
    btn.classList.add("opacity-50", "pointer-events-none")
    btn.innerHTML = `<svg class="w-4 h-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Starting run...</span>`

    try {
      const message = this.hasMessageTarget ? this.messageTarget.value : ""
      const body = { message }
      if (this.hasUserIdTarget && this.userIdTarget.value) {
        body.user_id = this.userIdTarget.value
      }
      if (this.hasMetaConnectIdTarget && this.metaConnectIdTarget.value) {
        body.meta_connect_id = this.metaConnectIdTarget.value
      }

      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(body),
      })

      const json = await response.json()

      if (json.success && json.data?.redirect_to) {
        // Update spinner text for navigation phase
        btn.innerHTML = `<svg class="w-4 h-4 animate-spin shrink-0" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Opening run...</span>`

        // Save message to localStorage history
        this.saveMessage(message)

        // Persist message in URL for browser back
        const currentUrl = new URL(window.location)
        currentUrl.searchParams.set("tab", "test")
        currentUrl.searchParams.set("test_message", message)
        history.replaceState({}, "", currentUrl)

        window.Turbo.visit(json.data.redirect_to)
      } else {
        this.resetButton(btn, originalHTML)
        this.showError(json.error || "Failed to start run")
      }
    } catch (err) {
      this.resetButton(btn, originalHTML)
      this.showError(`Request failed: ${err.message}`)
    }
  }

  useHistory(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const messages = this.loadHistory()
    if (messages[index] && this.hasMessageTarget) {
      this.messageTarget.value = messages[index]
    }
  }

  // Private

  resetButton(btn, html) {
    btn.classList.remove("opacity-50", "pointer-events-none")
    btn.innerHTML = html
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  get storageKey() {
    return `agent_test_messages_${this.agentIdValue}`
  }

  saveMessage(message) {
    if (!message.trim()) return
    const history = this.loadHistory()
    const filtered = history.filter(m => m !== message)
    filtered.unshift(message)
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(filtered.slice(0, 5)))
    } catch { /* localStorage full or unavailable */ }
  }

  loadHistory() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || "[]")
    } catch { return [] }
  }

  renderHistory() {
    if (!this.hasHistoryTarget || !this.hasHistoryListTarget) return
    const messages = this.loadHistory()
    if (messages.length === 0) {
      this.historyTarget.classList.add("hidden")
      return
    }

    this.historyTarget.classList.remove("hidden")
    this.historyListTarget.innerHTML = messages.map((msg, i) => {
      const preview = msg.length > 50 ? msg.substring(0, 50) + "..." : msg
      return `<button type="button" data-action="click->agent-test-run#useHistory" data-index="${i}"
        class="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 truncate max-w-[200px] cursor-pointer"
        title="${msg.replace(/"/g, '&quot;')}">${preview}</button>`
    }).join("")
  }
}
